import {ThumbsCarousel} from '@/_application/core/components/ui/thumb-carousel/thumb-carousel';
import {ProductDetailPrice} from '@/_application/modules/products/details/product-detail-price/product-detail-price';
import ProductDetailsBasicInfo from '@/_application/modules/products/details/product-details-basic-info';
import ProductDetailsSocialMedia from '@/_application/modules/products/details/product-details-social-media';
import VariationGroups from '@/_application/modules/products/details/variation-groups';
import {Product} from '@/_application/modules/products/model/product';
import {isProductInStock} from '@/_application/modules/products/model/stock';
import {useProductBadgesBySku} from '@/framework/product';
import {useAttributes} from '@/store/attributes/attributes.context';
import {GiftsCartProvider} from '@/store/gifts-cart/gifts-cart.context';
import {stickyShortDetailsAtom} from '@/store/sticky-short-details-atom';
import {Divider} from '@kidslabs/mario-ui-nextjs-react-core';
import {ProductBadge, ProductImage, ProductVideo} from '__generated__/__types__';
import {useAtom} from 'jotai';
import {isEmpty} from 'lodash';
import {useTranslation} from 'next-i18next';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useIntersection} from 'react-use';
import {ProductLabel} from '@/_application/modules/products/components/labels/product-label';
import ErrorAddToCartMessage from '@/_application/modules/products/add-to-cart/error-add-to-cart-message';
import {highestPriorityBadgeActive} from '@/_application/modules/products/model/product-badge-active';
import {getHiddenLabelsInFlashSale} from '@/_application/modules/flash-sales/helper/flash-sale';
import {useProductDetailCountdownFlashsale} from '@/framework/product-detail-countdown-flashsale';
import {AddToCart, CartCounterButton, CartCounterButtonEnum, ProductGift} from '@kidslabs/module-flash-sale';
import {AddToCartErrorTypeEnum} from '@kidslabs/module-quote';
import type {ProductModel} from '@kidslabs/module-catalog';
import {useModalAction} from '@/_application/core/components/ui/modal/modal.context';
import {useSession} from 'next-auth/react';

const INTERSECTION_RATIO_THRESHOLD = 0.999;

type Props = {
    product: Product;
    mediaGallery: (ProductImage | ProductVideo)[];
};

const Details: React.FC<Props> = ({product, mediaGallery}) => {
    const {t} = useTranslation('common');
    const {status} = useSession();
    const {openModal} = useModalAction();
    const {badges} = useProductBadgesBySku({sku: product.sku ?? ''});

    const [errorAddToCart, setErrorAddToCart] = useState<AddToCartErrorTypeEnum | null>(null);

    const [, setShowStickyShortDetails] = useAtom(stickyShortDetailsAtom);
    const intersectionRef = useRef<HTMLDivElement>(null);
    const intersection = useIntersection(intersectionRef as React.RefObject<HTMLElement>, {
        root: null,
        rootMargin: '0px',
        threshold: 1,
    });

    const productBadges = badges.filter(item => item) ?? [];
    const badgeRendering = highestPriorityBadgeActive(productBadges as ProductBadge[]);

    useEffect(() => {
        if (!intersection) {
            return;
        }

        if (intersection.intersectionRatio > INTERSECTION_RATIO_THRESHOLD) {
            setShowStickyShortDetails(false);
        } else {
            setShowStickyShortDetails(true);
        }
    }, [intersection]);

    const {selectedVariant, unselectedOptions, setInitialData} = useAttributes();
    const hasVariations = product.__typename === 'ConfigurableProduct';
    const productOptions = hasVariations ? (product?.configurable_options ?? []) : [];
    const selectedProduct = selectedVariant ? (selectedVariant.product as Product) : product;

    const {
        getProductDetailCountdownFlashsale,
        data: flashSaleData,
        loading: isFlashSaleLoading,
    } = useProductDetailCountdownFlashsale();

    useEffect(() => {
        if (selectedProduct.id) {
            getProductDetailCountdownFlashsale(selectedProduct.id);
        }
    }, [selectedProduct.id]);

    useEffect(() => {
        if (hasVariations) {
            setInitialData(productOptions, product?.variants ?? []);
        }
    }, [hasVariations, product]);

    const selectedMediaGallery = isEmpty(selectedVariant?.product?.media_gallery)
        ? mediaGallery
        : (selectedVariant?.product?.media_gallery as (ProductImage | ProductVideo)[]);

    const sortedGallery = [...selectedMediaGallery].sort((a, b) => {
        const positionA = a.position ?? 0;
        const positionB = b.position ?? 0;
        return positionA - positionB;
    });

    const handleRemoveErrorMessage = () => {
        setErrorAddToCart(null);
    };

    const handleAuthenticationGuard = useCallback(() => {
        openModal('OTP_LOGIN');
    }, [openModal]);

    return (
        <article className="h-full min-h-40 rounded-lg bg-primary-0">
            <div className="grid grid-cols-12 gap-x-4 gap-y-10 p-5">
                <div className="col-span-12 px-4 lg:col-span-6">
                    <div className="product-gallery relative min-h-129">
                        {sortedGallery && (
                            <div className="product-gallery">
                                <ThumbsCarousel badge={badgeRendering} gallery={sortedGallery} product={product} />
                            </div>
                        )}

                        <div className="absolute right-0 top-0 z-10">
                            {!isFlashSaleLoading && (
                                <ProductLabel
                                    product={selectedProduct}
                                    size={'large'}
                                    saleLabel="percent"
                                    hiddenLabels={getHiddenLabelsInFlashSale(flashSaleData)}
                                />
                            )}
                        </div>
                    </div>

                    <div className="mt-2">
                        <ProductDetailsSocialMedia product={product} />
                    </div>
                </div>

                <div className="col-span-12 flex flex-col items-start lg:col-span-6">
                    <div className="flex w-full flex-col" ref={intersectionRef}>
                        <ProductDetailsBasicInfo product={product} />
                        <Divider />
                        <GiftsCartProvider page="detail">
                            <ProductDetailPrice
                                product={product}
                                flashSaleData={flashSaleData}
                                isFlashSaleLoading={isFlashSaleLoading}
                            />
                            <Divider />
                            {hasVariations && (
                                <VariationGroups
                                    emitSelectOptionAction={handleRemoveErrorMessage}
                                    productOptions={productOptions}
                                />
                            )}

                            <ProductGift product={selectedProduct as ProductModel} />

                            {errorAddToCart && unselectedOptions && (
                                <ErrorAddToCartMessage type={errorAddToCart} unselectedOptions={unselectedOptions} />
                            )}

                            {/* CartCounterButton - Sticky counter for gift promotions (same as mobile TopStickyBlock) */}
                            <div className="my-2">
                                <CartCounterButton
                                    showIf
                                    variant={CartCounterButtonEnum.COUNTER}
                                    product={selectedProduct as ProductModel}
                                />
                            </div>

                            <div className="mt-6 flex flex-col items-center md:mt-2 lg:flex-row">
                                <div className="mb-3 w-full lg:mb-0">
                                    {isProductInStock(product) ? (
                                        <AddToCart
                                            product={product as ProductModel}
                                            variant="counter"
                                            variation={selectedVariant}
                                            disabled={false}
                                            emitErrorState={setErrorAddToCart}
                                            authenticationGuard={{
                                                isAuthenticated: status === 'authenticated',
                                                openLoginModal: handleAuthenticationGuard,
                                            }}
                                        />
                                    ) : (
                                        <div className="rounded bg-red-500 px-3 py-2 text-sm text-white">
                                            {t('text-out-stock')}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </GiftsCartProvider>
                    </div>
                </div>
            </div>
        </article>
    );
};

export default Details;
